'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import postHog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { ReactNode, useEffect } from 'react'

import { env } from '@/env'
import { PHEventName } from '@/lib/posthog/events'

const isPlaygroundRoute =
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/playground')

if (typeof window !== 'undefined' && !isPlaygroundRoute) {
  postHog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    // Enable debug mode in development
    loaded: (postHog) => {
      if (process.env.NODE_ENV === 'development') postHog.debug(false)
    },
    debug: false,
    person_profiles: 'always',
    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    persistence: 'memory',
    capture_pageleave: false,
    cross_subdomain_cookie: true,
    enable_recording_console_log: true,
    autocapture: false,
    disable_session_recording: false,
    disable_surveys: true,
    enable_heatmaps: false,
    disable_scroll_properties: true,
    disable_web_experiments: true,
    rageclick: false,
    capture_heatmaps: false,
    capture_performance: false,
    capture_dead_clicks: false,
  })
}

type PHViewCaptureProps = {
  eventName?: PHEventName | string
  pageName?: string
  setProps?: Record<string, any>
  setOnceProps?: Record<string, any>
}

export function PHViewCapture({
  eventName = PHEventName.PAGE_VIEW,
  pageName,
  setProps,
  setOnceProps,
}: PHViewCaptureProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (isPlaygroundRoute) {
      return
    }

    if (pathname && postHog) {
      let url = window.origin + pathname
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`
      }
      postHog.capture(eventName.toString(), {
        $current_url: url,
        ...(pageName ? { pageName: pageName } : {}),
        ...(setProps ? { $set: setProps } : {}),
        ...(setOnceProps ? { $set_once: setOnceProps } : {}),
      })
    }
  }, [eventName, pageName, pathname, searchParams, setOnceProps, setProps])

  return null
}

type Props = {
  children: ReactNode
}

export function PHProvider({ children }: Props) {
  if (isPlaygroundRoute) {
    return children
  }

  return <PostHogProvider client={postHog}>{children}</PostHogProvider>
}
